/** Wire contract of nyce-logic-auth (`/api/auth/v1`). Admin DTOs are camelCase; the token surface is snake_case. */

export type TenantStatus = 'Active' | 'Suspended' | 'Deleted';
export type IdentityProviderType = 'Workserver' | 'Database';
export type LocalUserStatus = 'Active' | 'Disabled';

export const TENANT_STATUSES: TenantStatus[] = ['Active', 'Suspended', 'Deleted'];
export const IDENTITY_PROVIDERS: IdentityProviderType[] = ['Workserver', 'Database'];
export const USER_STATUSES: LocalUserStatus[] = ['Active', 'Disabled'];

export interface TokenRequest {
  username: string;
  password: string;
  tenantId: string;
  client?: string;
  warehouse?: string;
  clientGroup?: string;
  languageCode?: string;
  printMachine?: string;
  ticket?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in?: number;
  token_type?: string;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PageQuery {
  page?: number;
  pageSize?: number;
}

/** An id another system uses for a customer or tenant (AUTH §10.8): `system` is a lowercase slug, `value` exact. */
export interface Alias {
  system: string;
  value: string;
  createdAt: string;
}
export interface AliasInput {
  system: string;
  value: string;
}
/** `GET /aliases/{system}/{value}`: the customer, and for a tenant alias also the tenant, the pair names. */
export interface AliasMatch extends AliasInput {
  customerId: string;
  customerName: string;
  tenantId?: string | null;
  tenantName?: string | null;
}

export interface Customer {
  id: string;
  name: string;
  /** Absent from AUTH builds before aliases. */
  aliases?: Alias[];
}
export interface CustomerInput {
  name: string;
}

export interface TenantClaim {
  id: string;
  type: string;
  value: string;
}
export interface ClaimInput {
  type: string;
  value: string;
}

/** Per-tenant role → scopes mapping; narrows the tenant's scopes for users holding the role (AUTH §4.3). */
export interface TenantRole {
  id: string;
  name: string;
  scopes: string[];
  /** Set when the role was provisioned as a SCIM group. */
  externalId?: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface TenantRoleInput {
  name: string;
  scopes: string[];
}

export interface Tenant {
  id: string;
  name: string;
  status: TenantStatus;
  identityProvider: IdentityProviderType;
  identityProviderUrl: string;
  customerId: string;
  customerName?: string | null;
  createdAt: string;
  updatedAt: string;
  claims: TenantClaim[];
  roles: TenantRole[];
  /** Absent from AUTH builds before aliases. */
  aliases?: Alias[];
}
export interface CreateTenantInput {
  name: string;
  identityProvider: IdentityProviderType;
  identityProviderUrl: string;
  customerId: string;
}
export interface UpdateTenantInput extends CreateTenantInput {
  status: TenantStatus;
}

export interface ServiceKeyInfo {
  id: string;
  createdAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
}
/** `POST .../service-keys` — the raw key, returned exactly once. */
export interface IssuedServiceKey {
  service_key: string;
}
/** `POST .../provisioning-keys` — the raw SCIM provisioning key, returned exactly once. */
export interface IssuedProvisioningKey {
  provisioning_key: string;
}

/**
 * Machine identity of a tenant (AUTH §10.9): `client_credentials` → 15-min JWT with `sub=client:<id>`,
 * `token_use=service`; scopes = requested ∩ client ∩ tenant claims.
 */
export type ServiceClientStatus = 'Active' | 'Disabled';
export interface ServiceClient {
  id: string;
  /** The OAuth `client_id` (same GUID as `id`). */
  clientId: string;
  tenantId: string;
  /** Lowercase slug, unique per tenant (duplicate → 409). */
  name: string;
  description?: string | null;
  scopes: string[];
  status: ServiceClientStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lastUsedAt?: string | null;
  /** Secrets neither revoked nor expired. */
  activeSecrets: number;
  /** On create/update only: scopes the tenant does not grant (stored, but not issued until it does). */
  warnings?: string[];
}
export interface CreateServiceClientInput {
  name: string;
  description?: string | null;
  scopes: string[];
}
export interface UpdateServiceClientInput extends CreateServiceClientInput {
  status: ServiceClientStatus;
}
export interface ClientSecretInfo {
  id: string;
  createdAt: string;
  createdBy: string;
  expiresAt?: string | null;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  active: boolean;
}
/** `POST .../clients/{id}/secrets` — the raw secret, returned exactly once. At most 2 active secrets per client. */
export interface IssuedClientSecret {
  client_id: string;
  client_secret: string;
  secret_id: string;
  expires_at?: string | null;
}

/** `GET /audit` row (AUTH §15): credential and alias changes, newest first. */
export interface AuthAuditEntry {
  id: string;
  at: string;
  actor: string;
  actorTenantId?: string | null;
  ip?: string | null;
  /** `client.create|update|delete`, `client_secret.issue|revoke`, `service_key.*`, `tenant_alias.*` … */
  action: string;
  targetType: string;
  targetId: string;
  tenantId?: string | null;
  customerId?: string | null;
  details?: Record<string, unknown> | null;
}
export interface AuditQuery {
  tenantId?: string;
  customerId?: string;
  action?: string;
  actor?: string;
  targetId?: string;
  since?: string;
  /** The oldest `at` of the previous page. */
  before?: string;
  /** 1–500 (AUTH default 100). */
  limit?: number;
}

export interface UserRole {
  id: string;
  role: string;
}
export interface LocalUser {
  id: string;
  tenantId: string;
  username: string;
  name?: string | null;
  email?: string | null;
  /** Set when the user was provisioned over SCIM (or created by a federated sign-in: the Entra object id). */
  externalId?: string | null;
  /** `saml` = created by a federated sign-in (Entra-only tenant). */
  origin?: string | null;
  /** Last federated sign-in. */
  lastSignInAt?: string | null;
  status: LocalUserStatus;
  createdAt: string;
  updatedAt: string;
  roles: UserRole[];
}
export interface CreateUserInput {
  username: string;
  password: string;
  name?: string | null;
  roles: string[];
}
export interface UpdateUserInput {
  name?: string | null;
  status: LocalUserStatus;
}

/** RFC 7807 body (ValidationProblemDetails carries `errors`); the rate limiter answers `{ error }`. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
  error?: string;
}

/** Federated sign-in of a Database tenant through a SAML IdP (Entra ID): `GET/PUT/DELETE /tenants/{id}/saml`. */
export type SamlUserMatch = 'Username' | 'ExternalId';
/** Where the tenant's users come from: SCIM / admin (`Existing`) or the sign-in itself (`Assertion`, Entra-only). */
export type SamlUserSource = 'Existing' | 'Assertion';
export const DEFAULT_ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

export interface SamlSettings {
  /** What the IdP needs from AUTH (Entra: Basic SAML Configuration); `entityId` is also the metadata URL. */
  sp: { entityId: string; acsUrl: string; metadataUrl: string };
  configured: boolean;
  enabled: boolean;
  metadataUrl?: string | null;
  matchBy: SamlUserMatch;
  /** False = sign-in only through the IdP while enabled (password `/token` refused). */
  allowPassword: boolean;
  userSource: SamlUserSource;
  /** Assertion: the attribute whose values become roles (Entra app roles by default). */
  roleClaim: string;
  /** Assertion: refuse a sign-in without any role. */
  requireRole: boolean;
  /** Assertion: refresh chains end this long after sign-in (0 = never). */
  reauthenticateHours: number;
  idp?: {
    entityId: string;
    ssoUrl: string;
    certificates: { subject: string; thumbprint: string; notAfter: string }[];
  } | null;
  /** Last successful read of `metadataUrl` (AUTH re-reads it daily and when a response fails to verify). */
  metadataFetchedAt?: string | null;
  /** Last attempt, and why it failed (the stored IdP stays in use). */
  metadataCheckedAt?: string | null;
  metadataError?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface SamlSettingsInput {
  enabled: boolean;
  /** One of the two, or neither to keep the stored metadata; a URL is fetched on every save. */
  metadataUrl?: string;
  metadataXml?: string;
  matchBy: SamlUserMatch;
  allowPassword: boolean;
  userSource: SamlUserSource;
  roleClaim?: string;
  requireRole: boolean;
  reauthenticateHours: number;
}

/** A browser app allowed to use AUTH's `/authorize` (`/login-apps/{id}`), with exact redirect URIs. */
export interface LoginApp {
  id: string;
  name: string;
  redirectUris: string[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}
